import React, { useState } from 'react';
import { Calendar as CalendarIcon, MapPin, Clock, Users, Plus } from 'lucide-react';
import { events } from '../../data/mockData';
interface EventListProps {
  tenantId: string;
  onNavigate: (page: string, id?: string) => void;
}
export function EventList({
  tenantId,
  onNavigate
}: EventListProps) {
  const [activeTab, setActiveTab] = useState('all');
  const tenantEvents = events.filter(e => e.tenantId === tenantId);
  // Sort: Live first, then upcoming by date, then past by date descending
  const sortedEvents = [...tenantEvents].sort((a, b) => {
    if (a.status === 'live' && b.status !== 'live') return -1;
    if (a.status !== 'live' && b.status === 'live') return 1;
    const dateA = new Date(a.date).getTime();
    const dateB = new Date(b.date).getTime();
    if (a.status === 'past' && b.status === 'past') return dateB - dateA;
    return dateA - dateB;
  });
  const filteredEvents = sortedEvents.filter(e => {
    if (activeTab === 'all') return true;
    return e.type === activeTab;
  });
  return <div className="space-y-6" data-id="element-416">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-417">
        <div className="flex overflow-x-auto hide-scrollbar gap-2 pb-2 sm:pb-0 w-full sm:w-auto" data-id="element-418">
          {[{
          id: 'all',
          label: 'Alle Events'
        }, {
          id: 'homeGame',
          label: 'Heimspiele'
        }, {
          id: 'business',
          label: 'Business'
        }, {
          id: 'networking',
          label: 'Networking'
        }, {
          id: 'vip',
          label: 'VIP'
        }].map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${activeTab === tab.id ? 'bg-slate-800 text-white' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'}`} data-id="element-419">
              {tab.label}
            </button>)}
        </div>

        <button className="flex items-center gap-2 px-4 py-2 bg-emerald-500 text-white rounded-lg text-sm font-medium hover:bg-emerald-600 transition-colors shadow-sm whitespace-nowrap" data-id="element-420">
          <Plus size={16} data-id="element-421" />
          Event erstellen
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-422">
        {filteredEvents.map(event => <div key={event.id} onClick={() => onNavigate('event-detail', event.id)} className={`bg-white rounded-xl border shadow-sm hover:shadow-md transition-all cursor-pointer group flex flex-col sm:flex-row overflow-hidden ${event.status === 'live' ? 'border-emerald-400 ring-1 ring-emerald-400' : 'border-slate-200'}`} data-id="element-423">
            <div className="w-full sm:w-48 h-48 sm:h-auto bg-slate-50 border-r border-slate-100 flex flex-col items-center justify-center p-6 relative" data-id="element-424">
              {event.status === 'live' && <div className="absolute top-3 left-3 flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-700" data-id="element-425">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse-fast" data-id="element-426"></span>
                  Live
                </div>}
              <span className="text-5xl mb-2" data-id="element-427">{event.imageEmoji}</span>
              <span className="text-sm font-bold text-slate-900 text-center" data-id="element-428">
                {new Date(event.date).toLocaleDateString('de-DE', {
              day: '2-digit',
              month: 'short'
            })}
              </span>
            </div>

            <div className="p-6 flex-1 flex flex-col" data-id="element-429">
              <div className="flex items-start justify-between mb-2" data-id="element-430">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold uppercase tracking-wider ${event.type === 'homeGame' ? 'bg-red-100 text-red-700' : event.type === 'business' ? 'bg-blue-100 text-blue-700' : event.type === 'vip' ? 'bg-purple-100 text-purple-700' : 'bg-emerald-100 text-emerald-700'}`} data-id="element-431">
                  {event.type === 'homeGame' ? 'Heimspiel' : event.type}
                </span>

                {event.status === 'past' && <span className="text-xs font-medium text-slate-400" data-id="element-432">
                    Vergangen
                  </span>}
              </div>

              <h3 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-emerald-600 transition-colors line-clamp-2" data-id="element-433">
                {event.title}
              </h3>

              <div className="space-y-2 text-sm text-slate-600 mb-4" data-id="element-434">
                <div className="flex items-center gap-2" data-id="element-435">
                  <Clock size={16} className="text-slate-400" data-id="element-436" />
                  {event.time} Uhr
                </div>
                <div className="flex items-center gap-2" data-id="element-437">
                  <MapPin size={16} className="text-slate-400" data-id="element-438" />
                  <span className="truncate" data-id="element-439">{event.location}</span>
                </div>
              </div>

              <div className="mt-auto pt-4 border-t border-slate-100 flex items-center justify-between" data-id="element-440">
                <div className="flex items-center gap-2 text-sm font-medium text-slate-700" data-id="element-441">
                  <Users size={16} className="text-slate-400" data-id="element-442" />
                  {event.attendeeCount} / {event.maxAttendees}
                </div>
                <span className="text-sm font-medium text-emerald-600 group-hover:translate-x-1 transition-transform" data-id="element-443">
                  Details &rarr;
                </span>
              </div>
            </div>
          </div>)}
      </div>
    </div>;
}